import { faMinus, faPlus } from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { Accordion, AccordionDetails, AccordionSummary, Box, Typography, useTheme } from '@mui/material'
import { format } from 'date-fns'
import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { FaqItem } from '../../api/faq'
import { parseHtmlContent } from '../../utils/htmlRenderer'

interface FaqListProps {
    faqs: FaqItem[]
}

const FaqList: React.FC<FaqListProps> = ({ faqs }) => {
    const { t } = useTranslation()
    const theme = useTheme()
    const [expanded, setExpanded] = useState<string | false>(false)

    const handleChange = (panel: string) => (event: React.SyntheticEvent, isExpanded: boolean) => {
        setExpanded(isExpanded ? panel : false)
    }

    // Grouper les FAQs par catégorie
    const faqsByCategory = faqs.reduce<Record<string, FaqItem[]>>((acc, faq) => {
        const category = faq.category || t('faq.categories.other')
        if (!acc[category]) {
            acc[category] = []
        }
        acc[category].push(faq)
        return acc
    }, {})

    // Trier les catégories par ordre alphabétique
    const sortedCategories = Object.keys(faqsByCategory).sort((a, b) => a.localeCompare(b))

    return (
        <Box sx={{ px: { xs: 2, md: 4 }, py: 1 }} data-testid="faq-list">
            {sortedCategories.map((category, catIndex) => (
                <Box key={category} mb={catIndex < sortedCategories.length - 1 ? 6 : 0}>
                    <Typography
                        variant="h5"
                        component="h2"
                        sx={{
                            mb: 3,
                            fontWeight: 700,
                            fontSize: '2.2rem',
                            color: 'var(--primary-color, #B40000)',
                            position: 'relative',
                            display: 'inline-block',
                            '&:after': {
                                content: '""',
                                position: 'absolute',
                                bottom: -8,
                                left: 0,
                                width: '40px',
                                height: '3px',
                                backgroundColor: 'var(--primary-color, #B40000)',
                                borderRadius: '2px',
                            },
                        }}
                    >
                        {category}
                    </Typography>

                    <Box sx={{ '& > * + *': { mt: 1 } }}>
                        {[...faqsByCategory[category]]
                            .sort((a, b) => a.order - b.order)
                            .map((faq) => (
                                <Accordion
                                    key={faq.id}
                                    expanded={expanded === `panel-${faq.id}`}
                                    onChange={handleChange(`panel-${faq.id}`)}
                                    disableGutters
                                    elevation={0}
                                    sx={{
                                        mb: 1,
                                        borderRadius: 2,
                                        overflow: 'hidden',
                                        border: `1px solid ${theme.palette.divider}`,
                                        transition: 'all 0.3s ease-in-out',
                                        '&:hover': {
                                            borderColor: 'var(--primary-color, #B40000)',
                                            boxShadow: '0 4px 12px rgba(180, 0, 0, 0.2)',
                                        },
                                        '&.Mui-expanded': {
                                            margin: '8px 0',
                                        },
                                    }}
                                >
                                    <AccordionSummary
                                        expandIcon={
                                            <FontAwesomeIcon
                                                icon={expanded === `panel-${faq.id}` ? faMinus : faPlus}
                                                style={{
                                                    fontSize: '1.2rem',
                                                    color: 'var(--primary-color, #B40000)',
                                                    transition: 'transform 0.3s ease-in-out',
                                                }}
                                            />
                                        }
                                        sx={{
                                            px: 3,
                                            py: 0,
                                            minHeight: '64px',
                                            '& .MuiAccordionSummary-content': {
                                                my: 2,
                                                '&.Mui-expanded': {
                                                    my: 2,
                                                },
                                            },
                                            backgroundColor:
                                                expanded === `panel-${faq.id}`
                                                    ? 'rgba(180, 0, 0, 0.05)'
                                                    : 'transparent',
                                            '&:hover': {
                                                backgroundColor: 'rgba(180, 0, 0, 0.08)',
                                            },
                                            borderBottom: '1px solid rgba(180, 0, 0, 0.1)',
                                        }}
                                    >
                                        <Typography
                                            variant="h6"
                                            component="div"
                                            sx={{
                                                fontWeight: 600,
                                                fontSize: '1.8rem',
                                                color: 'text.primary',
                                            }}
                                        >
                                            {faq.question}
                                        </Typography>
                                    </AccordionSummary>

                                    <AccordionDetails sx={{ pt: 2, pb: 3 }}>
                                        <Box
                                            sx={{
                                                '& p': {
                                                    fontSize: '1.6rem',
                                                    lineHeight: 1.6,
                                                    margin: '0 0 1em 0',
                                                    '&:last-child': { marginBottom: 0 },
                                                },
                                                '& ul, & ol': {
                                                    pl: 3,
                                                    '& li': { mb: 1 },
                                                },
                                            }}
                                        >
                                            {parseHtmlContent(faq.answer)}
                                        </Box>

                                        <Box mt={2}>
                                            <Typography
                                                variant="caption"
                                                color="text.secondary"
                                                component="div"
                                                sx={{ fontSize: '1rem' }}
                                            >
                                                {t('common.lastUpdated')}:{' '}
                                                {format(new Date(faq.updatedAt ?? faq.createdAt!), 'dd/MM/yyyy')}
                                            </Typography>
                                        </Box>
                                    </AccordionDetails>
                                </Accordion>
                            ))}
                    </Box>
                </Box>
            ))}
        </Box>
    )
}

export default FaqList
